/**
 * ClientCombobox.jsx — Searchable, addable client picker sourced from CRM Companies.
 *
 * Iter 117 — used in the New Project form (and anywhere else a "client = company"
 * needs to be picked). On open it loads /api/crm/companies once; user can type to
 * filter; if no exact match exists, an "+ Add «typed text» as a new company" row
 * appears which calls POST /api/crm/companies, then sets the value to the new
 * company name without leaving the form.
 *
 * Props:
 *   value: string — current client/company name
 *   onChange: (string) => void
 *   testid?: string
 *   required?: boolean
 *   placeholder?: string
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Plus, Loader2 } from 'lucide-react';
import { apiGet, apiPost } from '../lib/api';
import { toast } from 'sonner';

export default function ClientCombobox({ value, onChange, testid, required, placeholder = 'Search or add client…' }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value || '');
  const [companies, setCompanies] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [adding, setAdding] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  // Sync external value -> input query
  useEffect(() => { setQuery(value || ''); }, [value]);

  // Lazy-load companies on first open
  useEffect(() => {
    if (open && !loaded) {
      apiGet('/crm/companies')
        .then((r) => { setCompanies(Array.isArray(r) ? r : (r?.items || [])); setLoaded(true); })
        .catch(() => { toast.error('Could not load companies'); setLoaded(true); });
    }
  }, [open, loaded]);

  // Click-outside closes the popover
  useEffect(() => {
    const onDown = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const trimmed = (query || '').trim();
  const filtered = useMemo(() => {
    if (!trimmed) return companies.slice(0, 80);
    const q = trimmed.toLowerCase();
    return companies.filter((c) => (c.name || '').toLowerCase().includes(q)).slice(0, 80);
  }, [companies, trimmed]);

  const exactMatch = useMemo(
    () => companies.find((c) => (c.name || '').toLowerCase() === trimmed.toLowerCase()),
    [companies, trimmed]
  );
  const canAdd = trimmed.length >= 2 && !exactMatch;

  const commitName = (name) => {
    onChange(name);
    setQuery(name);
    setOpen(false);
  };

  const addNew = async () => {
    if (!canAdd || adding) return;
    setAdding(true);
    try {
      const created = await apiPost('/crm/companies', {
        name: trimmed, status: 'Active', industry: 'Construction',
      });
      setCompanies((prev) => [created, ...prev]);
      toast.success(`Added "${created.name}" to companies`);
      commitName(created.name);
    } catch (e) {
      // 409 → company already exists with same name (race) → just use the name
      const detail = e?.response?.data?.detail || '';
      if (String(detail).toLowerCase().includes('already exists')) {
        commitName(trimmed);
      } else {
        toast.error(detail || 'Could not add company');
      }
    } finally {
      setAdding(false);
    }
  };

  const onKey = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const max = filtered.length + (canAdd ? 1 : 0) - 1;
      setHighlight((h) => Math.min(max, h + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlight < filtered.length) {
        commitName(filtered[highlight].name);
      } else if (canAdd) {
        addNew();
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapRef} className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={query}
          placeholder={placeholder}
          required={required}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); setHighlight(0); }}
          onKeyDown={onKey}
          data-testid={testid}
          className="w-full px-3 py-2 pr-9 border border-slate-300 rounded-sm text-sm focus:border-[#3b4872] focus:ring-1 focus:ring-[#3b4872] focus:outline-none"
        />
        <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
      </div>

      {open && (
        <div className="absolute z-30 mt-1 w-full max-h-72 overflow-y-auto bg-white border border-slate-200 rounded-md shadow-lg" data-testid={`${testid || 'client-combo'}-popover`}>
          {!loaded ? (
            <div className="px-3 py-3 text-xs text-slate-500 flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Loading companies…</div>
          ) : (
            <>
              {filtered.length === 0 && !canAdd && (
                <div className="px-3 py-3 text-xs text-slate-500">Type at least 2 characters to add a new company.</div>
              )}
              {filtered.map((c, idx) => (
                <button
                  key={c.id || c.name}
                  type="button"
                  onMouseEnter={() => setHighlight(idx)}
                  onClick={() => commitName(c.name)}
                  data-testid={`${testid || 'client-combo'}-opt-${idx}`}
                  className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-slate-50 ${highlight === idx ? 'bg-slate-50' : ''}`}
                >
                  <div className="min-w-0">
                    <div className="font-display font-semibold text-slate-800 truncate">{c.name}</div>
                    {(c.industry || c.location || c.gst) && (
                      <div className="text-[10px] text-slate-500 truncate">
                        {[c.industry, c.location, c.gst].filter(Boolean).join(' · ')}
                      </div>
                    )}
                  </div>
                  {value === c.name && <Check size={13} className="text-emerald-600 flex-shrink-0" />}
                </button>
              ))}
              {canAdd && (
                <button
                  type="button"
                  onMouseEnter={() => setHighlight(filtered.length)}
                  onClick={addNew}
                  disabled={adding}
                  data-testid={`${testid || 'client-combo'}-add-new`}
                  className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 border-t border-slate-100 ${highlight === filtered.length ? 'bg-violet-50' : ''} hover:bg-violet-50 text-violet-700 disabled:opacity-50`}
                >
                  {adding ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
                  Add "<strong className="truncate max-w-[180px] inline-block align-bottom">{trimmed}</strong>" as a new company
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
